import { SHORTCUTS } from '@/constants/shortcuts';
import { getErrorCode } from '@/lib/error-utils';
import {
  CANVAS_SHORTCUT_CATALOG,
  type CanvasShortcutActionId,
  type CanvasShortcutCatalogItem,
} from '@/lib/canvas/canvas-keyboard-shortcuts';

export interface CanvasShortcutBinding {
  keyTokens: string[];
}

export type CanvasShortcutOverrideMap = Partial<
  Record<CanvasShortcutActionId, CanvasShortcutBinding>
>;

export const CANVAS_LOCKED_SHORTCUT_ACTION_IDS: ReadonlySet<CanvasShortcutActionId> = new Set([
  'space-pan',
  'disable-snap',
  'paste-media',
  'escape',
  'delete-node',
  'shortcuts-help',
]);

/** 必须与 server `CANVAS_REMAPPABLE_SHORTCUT_ACTION_IDS` 对齐。 */
export const CANVAS_REMAPPABLE_SHORTCUT_ACTION_IDS: ReadonlySet<CanvasShortcutActionId> = new Set(
  CANVAS_SHORTCUT_CATALOG.filter((item) => !CANVAS_LOCKED_SHORTCUT_ACTION_IDS.has(item.id)).map(
    (item) => item.id
  )
);

const LOCKED_CHORD_KEYS = new Set([
  chordKey(['Space']),
  chordKey(['Alt']),
  chordKey(['Mod', 'V']),
  chordKey(['Esc']),
]);

const CANVAS_PAGE_EDITOR_SHORTCUT_IDS = new Set([
  'NEW_PROJECT',
  'NEW_FOLDER',
  'TOGGLE_PROJECT_PANEL',
  'TOGGLE_RESOURCES_PANEL',
  'TOGGLE_PROPERTIES_PANEL',
]);

const BROWSER_RESERVED_CHORD_KEYS = new Set([
  chordKey(['Mod', 'W']),
  chordKey(['Mod', 'T']),
  chordKey(['Mod', 'N']),
  chordKey(['Mod', 'Q']),
  chordKey(['Mod', 'R']),
]);

const MODIFIER_TOKENS = new Set(['Mod', 'Shift', 'Alt']);
const SHIFT_FLEXIBLE_KEYS = new Set(['+', '?']);
const NAMED_KEYS = new Set(['Esc', 'Delete', 'Backspace', 'Space', '+', '-', '?']);
const TOKEN_ALIASES: Record<string, string> = {
  mod: 'Mod',
  meta: 'Mod',
  cmd: 'Mod',
  command: 'Mod',
  ctrl: 'Mod',
  control: 'Mod',
  shift: 'Shift',
  alt: 'Alt',
  option: 'Alt',
  esc: 'Esc',
  escape: 'Esc',
  delete: 'Delete',
  backspace: 'Backspace',
  space: 'Space',
  '+': '+',
  '-': '-',
  '?': '?',
};

export function resolveCanvasShortcutCatalog(
  overrides: CanvasShortcutOverrideMap = {}
): CanvasShortcutCatalogItem[] {
  return CANVAS_SHORTCUT_CATALOG.map((item) => {
    const override = overrides[item.id];
    if (!override?.keyTokens?.length || CANVAS_LOCKED_SHORTCUT_ACTION_IDS.has(item.id)) {
      return { ...item, overridden: false };
    }
    return {
      ...item,
      keyTokens: [...override.keyTokens],
      keyAlternatives: undefined,
      overridden: true,
    };
  });
}

export function chordKey(tokens: readonly string[]): string {
  return [...tokens]
    .map((token) => token.toLowerCase())
    .sort()
    .join('+');
}

export function canonicalizeShortcutTokens(tokens: unknown): string[] | null {
  if (!Array.isArray(tokens) || tokens.length === 0) return null;

  const normalized: string[] = [];
  const seen = new Set<string>();
  for (const raw of tokens) {
    if (typeof raw !== 'string' || !raw.trim()) return null;
    const token = canonicalizeShortcutToken(raw.trim());
    if (!token || seen.has(token)) return null;
    seen.add(token);
    normalized.push(token);
  }

  const keyTokens = normalized.filter((token) => !MODIFIER_TOKENS.has(token));
  if (keyTokens.length !== 1) return null;
  if (SHIFT_FLEXIBLE_KEYS.has(keyTokens[0])) {
    return normalized.filter((token) => token !== 'Shift');
  }
  return normalized;
}

function canonicalizeShortcutToken(raw: string): string | null {
  const alias = TOKEN_ALIASES[raw.toLowerCase()];
  if (alias) return alias;
  if (raw.length === 1 && /[a-zA-Z]/.test(raw)) return raw.toUpperCase();
  if (raw.length === 1 && /[0-9]/.test(raw)) return raw;
  if (NAMED_KEYS.has(raw) || MODIFIER_TOKENS.has(raw)) return raw;
  return null;
}

export function findCanvasShortcutConflict(
  actionId: CanvasShortcutActionId,
  tokens: readonly string[],
  overrides: CanvasShortcutOverrideMap
): CanvasShortcutCatalogItem | null {
  const nextOverrides: CanvasShortcutOverrideMap = {
    ...overrides,
    [actionId]: { keyTokens: [...tokens] },
  };
  const catalog = resolveCanvasShortcutCatalog(nextOverrides);
  const target = chordKey(tokens);
  for (const item of catalog) {
    if (item.id === actionId) continue;
    const chords = item.overridden
      ? [item.keyTokens]
      : [item.keyTokens, ...(item.keyAlternatives ?? [])];
    if (chords.some((chord) => chordKey(chord) === target)) {
      return item;
    }
  }
  return null;
}

export function isLockedCanvasShortcutChord(tokens: readonly string[]): boolean {
  return LOCKED_CHORD_KEYS.has(chordKey(tokens));
}

export function isCanvasShortcutActionLocked(actionId: CanvasShortcutActionId): boolean {
  return CANVAS_LOCKED_SHORTCUT_ACTION_IDS.has(actionId);
}

export function findCanvasPageEditorShortcutConflict(
  tokens: readonly string[]
): { id: string; name: string } | null {
  const target = chordKey(tokens);
  for (const shortcut of SHORTCUTS) {
    if (!CANVAS_PAGE_EDITOR_SHORTCUT_IDS.has(shortcut.id)) continue;
    const matchTokens: string[] = [];
    if (shortcut.match.metaKey || shortcut.match.ctrlKey) matchTokens.push('Mod');
    if (shortcut.match.altKey) matchTokens.push('Alt');
    if (shortcut.match.shiftKey) matchTokens.push('Shift');
    const key = shortcut.match.code?.startsWith('Key')
      ? shortcut.match.code.slice(3)
      : shortcut.match.key.toUpperCase();
    matchTokens.push(key);
    if (chordKey(matchTokens) === target) {
      return { id: shortcut.id, name: shortcut.name };
    }
  }
  return null;
}

export function isBrowserReservedShortcutChord(tokens: readonly string[]): boolean {
  return BROWSER_RESERVED_CHORD_KEYS.has(chordKey(tokens));
}

export function mergeHydrateCanvasOverrides(params: {
  pendingSync: boolean;
  localCanvas: CanvasShortcutOverrideMap;
  serverCanvas: CanvasShortcutOverrideMap;
}): CanvasShortcutOverrideMap {
  if (params.pendingSync) return params.localCanvas;
  return params.serverCanvas;
}

export function isRetryableShortcutSyncError(error: unknown): boolean {
  const code = getErrorCode(error);
  if (code === undefined) return true;
  if (code === 408 || code === 429) return true;
  return code >= 500;
}

export function sanitizeCanvasOverrideMap(
  input: CanvasShortcutOverrideMap | Record<string, CanvasShortcutBinding> | undefined
): CanvasShortcutOverrideMap {
  if (!input || typeof input !== 'object') return {};
  const next: CanvasShortcutOverrideMap = {};
  for (const item of CANVAS_SHORTCUT_CATALOG) {
    if (CANVAS_LOCKED_SHORTCUT_ACTION_IDS.has(item.id)) continue;
    const binding = (input as CanvasShortcutOverrideMap)[item.id];
    const tokens = canonicalizeShortcutTokens(binding?.keyTokens);
    if (!tokens) continue;
    if (isLockedCanvasShortcutChord(tokens)) continue;
    if (findCanvasShortcutConflict(item.id, tokens, next)) continue;
    next[item.id] = { keyTokens: tokens };
  }
  return next;
}
